/**
 * PATH: packages/drafting/src/panels/InputPanel.tsx
 * FILE_ID: DRAFTING-PANEL-INPUT-001
 * PURPOSE: Drawing properties — title, scale, paper, grid. (The sketch upload
 *   + AI generate flow moved to GeneratePanel in P3.)
 */
import type { DrawingMeta, IrUnit, PaperSize } from '../ir/schema'

const SCALES = ['1:1', '1:2', '1:5', '1:10', '1:20', '1:50', '1:100', '1:200']
const PAPERS: PaperSize[] = ['A0', 'A1', 'A2', 'A3', 'A4']
const UNITS: IrUnit[] = ['mm', 'cm', 'm', 'in']

export interface InputPanelProps {
  meta: DrawingMeta
  onMeta: (patch: Partial<DrawingMeta>) => void
}

const field = 'w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800'
const lbl = 'block text-xs font-medium text-slate-500 dark:text-slate-400'

export function InputPanel(props: InputPanelProps) {
  const { meta, onMeta } = props

  return (
    <section className="space-y-2.5">
      <h3 className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Drawing</h3>

      <label className="space-y-1 block">
        <span className={lbl}>Title</span>
        <input className={field} value={meta.title} onChange={(e) => onMeta({ title: e.target.value })} />
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="space-y-1 block">
          <span className={lbl}>Scale</span>
          <select className={field} value={meta.scale.ratio} onChange={(e) => onMeta({ scale: { ratio: e.target.value } })}>
            {SCALES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1 block">
          <span className={lbl}>Units</span>
          <select className={field} value={meta.units} onChange={(e) => onMeta({ units: e.target.value as IrUnit })}>
            {UNITS.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1 block">
          <span className={lbl}>Paper</span>
          <select
            className={field}
            value={typeof meta.paper === 'string' ? meta.paper : 'custom'}
            onChange={(e) => onMeta({ paper: e.target.value as PaperSize })}
          >
            {PAPERS.map((p) => (
              <option key={String(p)} value={String(p)}>{String(p)}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1 block">
          <span className={lbl}>Grid ({meta.units})</span>
          <input
            type="number"
            min={1}
            className={field}
            value={meta.grid.size}
            onChange={(e) => onMeta({ grid: { ...meta.grid, size: Math.max(1, Number(e.target.value) || 1) } })}
          />
        </label>
      </div>

      <label className="flex items-center gap-2 px-1 text-sm text-slate-600 dark:text-slate-300">
        <input
          type="checkbox"
          checked={meta.grid.snap}
          onChange={(e) => onMeta({ grid: { ...meta.grid, snap: e.target.checked } })}
        />
        Snap to grid
      </label>
    </section>
  )
}
